import { authFetch } from '../lib/authFetch';
import { buildQueryString } from '../lib/query';
import { resolveBase, json } from './client';
import type {
  BugReportListResponse,
  BugReportRecord,
  CreateBugReportPayload,
  UpdateBugReportPayload,
  BugSeverity,
  BugStatus,
} from '../types/bugReports';
import type { ApiErrorPayload, PaginationMeta } from './contracts';

export type ListBugReportsParams = {
  page?: number;
  page_size?: number;
  status?: BugStatus;
  severity?: BugSeverity;
  reporter_id?: string;
  search?: string;
  signal?: AbortSignal;
};

type BugReportApiResponse = ApiErrorPayload & {
  data?: BugReportRecord;
  items?: BugReportRecord[];
  pagination?: Partial<PaginationMeta>;
};

function ensurePagination(payload: BugReportApiResponse): BugReportListResponse['pagination'] {
  if (payload.pagination) {
    return {
      page: Number(payload.pagination.page) || 1,
      page_size: Number(payload.pagination.page_size) || 50,
      total: Number(payload.pagination.total) || 0,
      total_pages: Number(payload.pagination.total_pages) || 0,
    };
  }
  return {
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0,
  };
}

export async function listBugReports(apiBase: string, params: ListBugReportsParams = {}): Promise<BugReportListResponse> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      page: params.page,
      page_size: params.page_size,
      status: params.status,
      severity: params.severity,
      reporter_id: params.reporter_id,
      search: params.search,
    },
    { includeQuestionMark: true }
  );

  const response = await authFetch(`${base}/api/bugs${query}`, { signal: params.signal });
  const payload = await json<BugReportApiResponse>(response);

  if (payload?.status === 'success') {
    return {
      items: Array.isArray(payload.items) ? (payload.items as BugReportRecord[]) : [],
      pagination: ensurePagination(payload),
    };
  }

  if (Array.isArray(payload?.items)) {
    return {
      items: payload.items as BugReportRecord[],
      pagination: ensurePagination(payload),
    };
  }

  return {
    items: [],
    pagination: ensurePagination(payload),
  };
}

export async function getBugReport(apiBase: string, bugId: string, options: { signal?: AbortSignal } = {}): Promise<BugReportRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/bugs/${bugId}`, { signal: options.signal });
  const payload = await json<BugReportApiResponse>(response);

  if (payload?.status === 'success' && payload.data) {
    return payload.data as BugReportRecord;
  }

  if (payload?.data) {
    return payload.data as BugReportRecord;
  }

  return payload as unknown as BugReportRecord;
}

export async function createBugReport(apiBase: string, body: CreateBugReportPayload, options: { signal?: AbortSignal } = {}): Promise<BugReportRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/bugs`, {
    method: 'POST',
    body: JSON.stringify(body),
    signal: options.signal,
  });
  const payload = await json<BugReportApiResponse>(response);

  if (payload?.status === 'success' && payload.data) {
    return payload.data as BugReportRecord;
  }

  if (payload?.data) {
    return payload.data as BugReportRecord;
  }

  throw new Error(payload?.message || 'Failed to submit bug report');
}

export async function updateBugReport(apiBase: string, bugId: string, body: UpdateBugReportPayload, options: { signal?: AbortSignal } = {}): Promise<BugReportRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/bugs/${bugId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    signal: options.signal,
  });
  const payload = await json<BugReportApiResponse>(response);

  if (payload?.status === 'success' && payload.data) {
    return payload.data as BugReportRecord;
  }

  if (payload?.data) {
    return payload.data as BugReportRecord;
  }

  throw new Error(payload?.message || 'Failed to update bug report');
}
